<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import Paginator from 'primevue/paginator';
import ProgressSpinner from 'primevue/progressspinner';
import getAccountExceptions from '@gpx-spa/queries/src/getAccountExceptions';
import getAccountExceptionMetrics from '@gpx-spa/queries/src/getAccountExceptionMetrics';
import usePostAccountExceptionsBulkClosed from '@gpx-spa/queries/src/mutations/postAccountExceptionsBulkClosed';
import usePostAccountExceptionsBulkReopen from '@gpx-spa/queries/src/mutations/postAccountExceptionsBulkReopen';
import usePostAccountExceptionClosed from '@gpx-spa/queries/src/mutations/postAccountExceptionClosed';
import useDeleteAccountExceptionClosed from '@gpx-spa/queries/src/mutations/deleteAccountExceptionClosed';
import { downloadAccountExceptionsExport } from '@gpx-spa/queries/src/mutations/exportAccountExceptions';
import { getAccountDevices } from '@gpx-spa/queries';
import * as events from '@/events';
import type {
  AccountExceptionsListParams,
  ShipmentExceptionRow,
} from '@gpx-spa/queries/src/shipment-exception-types';
import ExceptionCloseFeedbackDialog from '@/components/shipment/exceptions/ExceptionCloseFeedbackDialog.vue';
import {
  EXCEPTIONS_FILTER_ALL,
  buildExceptionsListContextKey,
  exceptionSelectionAfterAction,
  isExceptionsAllFilter,
  pruneSelectedExceptions,
  resetExceptionsStatusScreenFilters,
  type ExceptionWorkspaceAction,
  type ExceptionWorkspaceActionOutcome,
} from '@/utils/exceptions-workspace-selection';
import ExceptionReopenConfirmDialog from '@/components/shipment/exceptions/ExceptionReopenConfirmDialog.vue';
import ExceptionsQueueGlyph from '@/components/shipment/exceptions/ExceptionsQueueGlyph.vue';
import ExceptionsWorkspaceCard from '@/components/shipment/exceptions/ExceptionsWorkspaceCard.vue';
import {
  namedTrackersFromIds,
  namedTrackerFromDeviceListRow,
} from '@/utils/named-tracker-set';
import {
  PVT_SPINNER_STROKE_WIDTH,
  PVT_SPINNER_STYLE_OVERLAY,
} from '@/constants/progress-spinner';

const props = defineProps<{
  accountId: number;
}>();

const emit = defineEmits<{
  switchToShipments: [];
}>();

type FilterOption = { label: string; value: string };

const statusQueue = ref<'open' | 'closed' | 'all'>('open');
const search = ref('');
const page = ref(1);
const perPage = ref(25);
const selected = ref<ShipmentExceptionRow[]>([]);
const feedbackOpen = ref(false);
const feedbackTargetIds = ref<number[]>([]);
const reopenOpen = ref(false);
const reopenTargetIds = ref<number[]>([]);

type StatusQueueKey = 'open' | 'closed' | 'all';

const statusQueues: {
  key: StatusQueueKey;
  label: string;
  glyph: 'open' | 'closed' | 'all';
  color: string;
}[] = [
  { key: 'open', label: 'Open', glyph: 'open', color: 'text-red-500' },
  { key: 'closed', label: 'Closed', glyph: 'closed', color: 'text-green-500' },
  {
    key: 'all',
    label: 'All Exceptions',
    glyph: 'all',
    color: 'text-gray-400',
  },
];

const typeQueues = [
  { key: 'off-route', label: 'Route Deviation', color: 'text-red-500' },
  { key: 'delayed-pickup', label: 'Delayed Pickup', color: 'text-amber-500' },
  {
    key: 'delayed-delivery',
    label: 'Delayed Delivery',
    color: 'text-orange-500',
  },
  { key: 'no-movement', label: 'No Movement', color: 'text-sky-500' },
  {
    key: 'tracker-separated',
    label: 'Tracker Separated',
    color: 'text-violet-500',
  },
] as const;

const severityOptions: FilterOption[] = [
  { label: 'All Severities', value: EXCEPTIONS_FILTER_ALL },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

const typeOptions: FilterOption[] = [
  { label: 'All Types', value: EXCEPTIONS_FILTER_ALL },
  ...typeQueues.map((t) => ({ label: t.label, value: t.key })),
];

const typeFilter = ref<FilterOption>(typeOptions[0]!);
const severityFilter = ref<FilterOption>(severityOptions[0]!);

const listContextKey = computed(() =>
  buildExceptionsListContextKey({
    status: statusQueue.value,
    type: typeFilter.value.value,
    severity: severityFilter.value.value,
    search: search.value,
  }),
);

const listParams = computed<AccountExceptionsListParams>(() => {
  const params: AccountExceptionsListParams = {
    type: isExceptionsAllFilter(typeFilter.value.value)
      ? undefined
      : typeFilter.value.value,
    severity: isExceptionsAllFilter(severityFilter.value.value)
      ? undefined
      : severityFilter.value.value,
    search: search.value.trim() || undefined,
    page: page.value,
    per_page: perPage.value,
  };
  if (statusQueue.value === 'open' || statusQueue.value === 'closed') {
    params.status = statusQueue.value;
  }
  return params;
});

const accountIdRef = computed(() => props.accountId);
const { data: metricsData } = getAccountExceptionMetrics(accountIdRef);
const { data: accountDevicesData } = getAccountDevices(accountIdRef, {
  device_status: 'active,provisioned,hidden',
});

const deviceNameSources = computed(() =>
  (accountDevicesData.value ?? []).map(namedTrackerFromDeviceListRow),
);

function trackersForException(row: ShipmentExceptionRow) {
  return namedTrackersFromIds(row.device_ids ?? [], deviceNameSources.value);
}

const {
  data: listData,
  isPending,
  isError,
  refetch,
} = getAccountExceptions(accountIdRef, listParams);

const rows = computed(() => listData.value?.data ?? []);
const total = computed(() => listData.value?.meta?.total ?? rows.value.length);

const metrics = computed(
  () =>
    metricsData.value ?? {
      open: 0,
      closed: 0,
      by_type: {} as Record<string, number>,
    },
);

const closeOne = usePostAccountExceptionClosed();
const bulkClose = usePostAccountExceptionsBulkClosed();
const reopenOne = useDeleteAccountExceptionClosed();
const bulkReopen = usePostAccountExceptionsBulkReopen();
const isClosing = computed(
  () => closeOne.isPending.value || bulkClose.isPending.value,
);
const isReopening = computed(
  () => reopenOne.isPending.value || bulkReopen.isPending.value,
);
const isBusy = computed(() => isClosing.value || isReopening.value);

/** Trackers-style: Close if any open; Reopen only when every selected row is closed. */
const selectedHaveAnyOpen = computed(() =>
  selected.value.some((r) => r.status === 'open'),
);
const selectedAllClosed = computed(
  () =>
    selected.value.length > 0 &&
    selected.value.every((r) => r.status === 'closed'),
);

function clearSelection() {
  selected.value = [];
  feedbackOpen.value = false;
  feedbackTargetIds.value = [];
  reopenOpen.value = false;
  reopenTargetIds.value = [];
}

function applySelectionAfterAction(
  action: ExceptionWorkspaceAction,
  outcome: ExceptionWorkspaceActionOutcome,
) {
  selected.value = exceptionSelectionAfterAction(
    action,
    outcome,
    selected.value,
  );
  if (selected.value.length === 0) {
    feedbackOpen.value = false;
    feedbackTargetIds.value = [];
    reopenOpen.value = false;
    reopenTargetIds.value = [];
  }
}

watch([statusQueue, typeFilter, severityFilter, search], () => {
  page.value = 1;
});

/** ENG-2796: bulk selection must not carry across screens / filters / search. */
watch(listContextKey, () => {
  clearSelection();
});

/** Drop selected rows that left the current result set (e.g. after close on Open). */
watch(
  rows,
  (nextRows) => {
    const before = selected.value.map((r) => r.id).join(',');
    const pruned = pruneSelectedExceptions(selected.value, nextRows);
    const after = pruned.map((r) => r.id).join(',');
    if (before !== after) {
      selected.value = pruned;
    }
  },
  { flush: 'post' },
);

function applyStatusScreenFilterReset() {
  const reset = resetExceptionsStatusScreenFilters();
  typeFilter.value =
    typeOptions.find((o) => o.value === reset.typeFilter) ?? typeOptions[0]!;
  severityFilter.value =
    severityOptions.find((o) => o.value === reset.severityFilter) ??
    severityOptions[0]!;
  search.value = reset.search;
  clearSelection();
}

function selectStatus(s: StatusQueueKey) {
  statusQueue.value = s;
  applyStatusScreenFilterReset();
}

function isStatusSelected(key: StatusQueueKey): boolean {
  if (key === 'closed') {
    return statusQueue.value === 'closed';
  }
  return (
    statusQueue.value === key &&
    isExceptionsAllFilter(typeFilter.value.value)
  );
}

function statusCount(key: StatusQueueKey): number {
  if (key === 'open') return metrics.value.open;
  if (key === 'closed') return metrics.value.closed;
  return metrics.value.open + metrics.value.closed;
}

function isTypeSelected(key: string): boolean {
  return typeFilter.value.value === key;
}

function queueRowClass(selected: boolean): string {
  return selected
    ? 'bg-gray-100 font-medium text-gray-900'
    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900';
}

function queueCountClass(selected: boolean): string {
  return selected ? 'bg-gray-200 text-gray-700' : 'bg-gray-100 text-gray-500';
}

function selectType(key: string) {
  statusQueue.value = 'open';
  const current = typeFilter.value.value;
  applyStatusScreenFilterReset();
  typeFilter.value =
    current === key
      ? typeOptions[0]!
      : (typeOptions.find((o) => o.value === key) ?? typeOptions[0]!);
}

const selectedIdSet = computed(() => new Set(selected.value.map((r) => r.id)));

const allPageSelected = computed(
  () =>
    rows.value.length > 0 &&
    rows.value.every((row) => selectedIdSet.value.has(row.id)),
);

const somePageSelected = computed(
  () =>
    !allPageSelected.value &&
    rows.value.some((row) => selectedIdSet.value.has(row.id)),
);

function isRowSelected(row: ShipmentExceptionRow): boolean {
  return selectedIdSet.value.has(row.id);
}

function toggleRowSelected(row: ShipmentExceptionRow) {
  if (isRowSelected(row)) {
    selected.value = selected.value.filter((r) => r.id !== row.id);
    return;
  }
  selected.value = [...selected.value, row];
}

function toggleSelectAllOnPage(checked: boolean) {
  if (!checked) {
    const pageIds = new Set(rows.value.map((r) => r.id));
    selected.value = selected.value.filter((r) => !pageIds.has(r.id));
    return;
  }
  const byId = new Map(selected.value.map((r) => [r.id, r]));
  for (const row of rows.value) {
    byId.set(row.id, row);
  }
  selected.value = [...byId.values()];
}

function openCloseDialog(ids: number[]) {
  const openIds = ids.filter((id) => {
    const row =
      selected.value.find((r) => r.id === id) ??
      rows.value.find((r) => r.id === id);
    return row?.status === 'open';
  });
  if (openIds.length === 0) return;
  feedbackTargetIds.value = openIds;
  feedbackOpen.value = true;
}

function openBulkCloseFromSelection() {
  openCloseDialog(selected.value.map((r) => r.id));
}

async function onConfirmClose(payload: {
  feedback: 'real_issue' | 'not_an_issue' | null;
  note: string;
}) {
  const ids = feedbackTargetIds.value;
  if (ids.length === 0) return;
  try {
    if (ids.length === 1) {
      await closeOne.mutateAsync({
        accountId: props.accountId,
        exceptionId: ids[0]!,
        feedback: payload.feedback,
        note: payload.note || null,
      });
    } else {
      await bulkClose.mutateAsync({
        accountId: props.accountId,
        exceptionIds: ids,
        feedback: payload.feedback,
        note: payload.note || null,
      });
    }
  } catch {
    return;
  }
  applySelectionAfterAction('close', 'success');
  await refetch();
}

function openReopenDialog(ids: number[]) {
  const closedIds = ids.filter((id) => {
    const row =
      selected.value.find((r) => r.id === id) ??
      rows.value.find((r) => r.id === id);
    return row?.status === 'closed';
  });
  if (closedIds.length === 0) return;
  reopenTargetIds.value = closedIds;
  reopenOpen.value = true;
}

function openBulkReopenFromSelection() {
  openReopenDialog(selected.value.map((r) => r.id));
}

async function onConfirmReopen() {
  const ids = reopenTargetIds.value;
  if (ids.length === 0) return;
  try {
    if (ids.length === 1) {
      await reopenOne.mutateAsync({
        accountId: props.accountId,
        exceptionId: ids[0]!,
      });
    } else {
      await bulkReopen.mutateAsync({
        accountId: props.accountId,
        exceptionIds: ids,
      });
    }
  } catch {
    return;
  }
  applySelectionAfterAction('reopen', 'success');
  await refetch();
}

async function onExport(selectedOnly: boolean) {
  events.trigger(
    selectedOnly
      ? events.names.SELECTED_SHIPMENT_EXCEPTIONS_EXPORTED
      : events.names.SHIPMENT_EXCEPTIONS_EXPORTED,
  );

  if (selectedOnly && selected.value.length > 0) {
    await downloadAccountExceptionsExport(props.accountId, {
      ids: selected.value.map((r) => r.id),
    });
    return;
  }
  const { page: _page, per_page: _perPage, ...filters } = listParams.value;
  await downloadAccountExceptionsExport(props.accountId, filters);
}

function onPage(e: { page: number; rows: number }) {
  page.value = e.page + 1;
  perPage.value = e.rows;
}
</script>

<template>
  <div class="flex min-h-0 flex-1 gap-0">
    <aside class="w-56 shrink-0 border-r border-neutral-200 bg-white px-3 py-2">
      <div class="mb-3 flex gap-1 border-b border-gray-100 pb-2">
        <button
          type="button"
          class="flex-1 rounded-md px-2 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-50"
          @click="emit('switchToShipments')"
        >
          Shipments
        </button>
        <button
          type="button"
          class="flex-1 rounded-md bg-gray-100 px-2 py-1.5 text-sm font-medium text-gray-900"
        >
          Exceptions
        </button>
      </div>
      <h3
        class="mb-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400"
      >
        Status
      </h3>
      <ul class="space-y-0.5">
        <li v-for="q in statusQueues" :key="q.key">
          <button
            type="button"
            class="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors"
            :class="queueRowClass(isStatusSelected(q.key))"
            @click="selectStatus(q.key)"
          >
            <span class="flex min-w-0 items-center gap-2">
              <ExceptionsQueueGlyph :name="q.glyph" :class="q.color" />
              <span class="truncate">{{ q.label }}</span>
            </span>
            <span
              class="min-w-[24px] shrink-0 rounded-full px-1.5 py-0.5 text-center text-[11px] font-medium"
              :class="queueCountClass(isStatusSelected(q.key))"
            >
              {{ statusCount(q.key) }}
            </span>
          </button>
        </li>
      </ul>

      <div class="my-3 border-t border-gray-200" />

      <h3
        class="mb-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400"
      >
        By Type
      </h3>
      <ul class="space-y-0.5">
        <li v-for="t in typeQueues" :key="t.key">
          <button
            type="button"
            class="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors"
            :class="queueRowClass(isTypeSelected(t.key))"
            @click="selectType(t.key)"
          >
            <span class="flex min-w-0 items-center gap-2">
              <ExceptionsQueueGlyph name="type" :class="t.color" />
              <span class="truncate">{{ t.label }}</span>
            </span>
            <span
              class="min-w-[24px] shrink-0 rounded-full px-1.5 py-0.5 text-center text-[11px] font-medium"
              :class="queueCountClass(isTypeSelected(t.key))"
            >
              {{ metrics.by_type[t.key] ?? 0 }}
            </span>
          </button>
        </li>
      </ul>
    </aside>

    <div class="flex min-w-0 flex-1 flex-col">
      <div
        class="flex flex-wrap items-center gap-2 border-b border-neutral-200 px-4 py-3"
      >
        <InputText
          v-model="search"
          placeholder="Search by shipment reference or device"
          class="min-w-56 flex-1"
          size="small"
        />
        <Select
          v-model="severityFilter"
          :options="severityOptions"
          option-label="label"
          size="small"
          class="w-40"
        />
        <Select
          v-model="typeFilter"
          :options="typeOptions"
          option-label="label"
          size="small"
          class="w-44"
        />
        <Button
          label="Export"
          size="small"
          severity="secondary"
          outlined
          @click="onExport(false)"
        />
      </div>

      <div
        v-if="selected.length > 0"
        class="flex items-center gap-3 border-b border-rose-100 bg-rose-50/80 px-4 py-2 text-sm"
      >
        <span class="font-medium">{{ selected.length }} selected</span>
        <Button
          v-if="selectedHaveAnyOpen"
          label="Close…"
          size="small"
          :disabled="isBusy"
          @click="openBulkCloseFromSelection"
        />
        <Button
          v-if="selectedAllClosed"
          label="Reopen"
          size="small"
          :disabled="isBusy"
          @click="openBulkReopenFromSelection"
        />
        <Button
          label="Export selected"
          size="small"
          severity="secondary"
          outlined
          @click="onExport(true)"
        />
        <button
          type="button"
          class="ml-auto text-xs text-neutral-600 underline"
          @click="clearSelection"
        >
          Clear selection
        </button>
      </div>

      <div class="relative min-h-0 flex-1 overflow-auto p-4">
        <div
          v-if="isPending"
          class="absolute inset-0 z-10 flex items-center justify-center bg-white/60"
        >
          <ProgressSpinner
            :style="PVT_SPINNER_STYLE_OVERLAY"
            :stroke-width="PVT_SPINNER_STROKE_WIDTH"
          />
        </div>
        <p v-else-if="isError" class="p-4 text-sm text-red-600">
          Could not load exceptions.
        </p>
        <div v-else-if="rows.length === 0" class="py-16 text-center">
          <p class="text-base font-semibold text-gray-900">
            No exceptions found
          </p>
          <p class="mt-1 text-sm text-gray-500">
            Try adjusting your search or filters
          </p>
        </div>
        <div v-else class="space-y-3">
          <div class="flex items-center gap-3 px-1">
            <Checkbox
              :model-value="allPageSelected"
              :indeterminate="somePageSelected"
              binary
              input-id="exceptions-select-all"
              aria-label="Select all exceptions on this page"
              @update:model-value="toggleSelectAllOnPage"
            />
            <span class="text-sm text-gray-500">
              {{ total }} exception{{ total === 1 ? '' : 's' }}
            </span>
          </div>
          <div class="space-y-2">
            <ExceptionsWorkspaceCard
              v-for="row in rows"
              :key="row.id"
              :row="row"
              :trackers="trackersForException(row)"
              :selected="isRowSelected(row)"
              :busy="isBusy"
              @toggle-select="toggleRowSelected(row)"
              @close="openCloseDialog([row.id])"
              @reopen="openReopenDialog([row.id])"
            />
          </div>
          <Paginator
            :first="(page - 1) * perPage"
            :rows="perPage"
            :total-records="total"
            :rows-per-page-options="[25, 50, 100]"
            @page="onPage"
          />
        </div>
      </div>
    </div>

    <ExceptionCloseFeedbackDialog
      v-model:visible="feedbackOpen"
      :count="feedbackTargetIds.length"
      :is-submitting="isClosing"
      @confirm="onConfirmClose"
    />
    <ExceptionReopenConfirmDialog
      v-model:visible="reopenOpen"
      :count="reopenTargetIds.length"
      :is-submitting="isReopening"
      @confirm="onConfirmReopen"
    />
  </div>
</template>
